import { useAtomValue } from 'jotai';
import { MenuButton, MenuItem } from '@carbon/react';
import { useCreateDcaWorkspace } from 'components/dca-workspace/use-create-dca-workspace';
import { tcWellsLoadingAtom } from 'pages/type-curves/type-curve-filters/atoms';
import style from './create-menu.module.scss';

export interface CreateMenuProps {
  /** Type curve header detail the page is showing — parent of a DCA workspace. */
  typeCurveHeaderDetailsId: number;
  /** UWIs currently passing the page filters, sent with a DCA workspace. */
  uwis: string[];
  /** The DCA scenario selected on the page; a new DCA workspace opens on it. */
  dcaScenarioId?: number | null;
  /** False while the type curve's workspaces collection href is missing. */
  canCreateTypeCurveWorkspace: boolean;
  onCreateTypeCurveWorkspace: () => void;
  onCreateInventoryWorkspace: () => void;
}

/**
 * The Type Curve details page's "Create" menu.
 *
 * Replaces the separate Create Type Curve Workspace and Create DCA Workspace
 * buttons — those actions live here now, alongside Inventory Workspace. Their
 * modals render as siblings of this menu on the page, not inside it.
 *
 * @param {CreateMenuProps} props - Launch context and the two modal openers.
 * @return {JSX.Element} The menu button.
 */
export const CreateMenu = ({
  typeCurveHeaderDetailsId,
  uwis,
  dcaScenarioId = null,
  canCreateTypeCurveWorkspace,
  onCreateTypeCurveWorkspace,
  onCreateInventoryWorkspace,
}: CreateMenuProps) => {
  const { createDcaWorkspace, loading: dcaLoading } = useCreateDcaWorkspace({
    parentType: 'TypeCurve::HeaderDetail',
    parentId: typeCurveHeaderDetailsId,
    additionalData: { uwis, dcaScenarioId },
  });

  // A DCA workspace is built from the wells currently passing the page filters,
  // so it can't be created until those have loaded and at least one matches.
  // While they're still loading the item shows a spinner rather than reading as
  // permanently unavailable.
  //
  // The spinner is drawn by CSS rather than passed as `renderIcon`: an icon
  // flips Carbon's `hasIcons` menu flag, which is one-way (MenuContext only ever
  // sets it true), and that permanently widens the menu to 12rem and indents
  // every item — the width never came back after the spinner went away.
  const wellsLoading = useAtomValue(tcWellsLoadingAtom);
  const dcaBusy = dcaLoading || wellsLoading;
  const dcaDisabled = dcaBusy || uwis.length === 0;

  return (
    <MenuButton
      label="Create"
      kind="primary"
      size="md"
      menuAlignment="bottom-end"
    >
      <MenuItem
        label="Type Curve Workspace"
        disabled={!canCreateTypeCurveWorkspace}
        onClick={onCreateTypeCurveWorkspace}
      />
      <MenuItem
        label="Inventory Workspace"
        onClick={onCreateInventoryWorkspace}
      />
      <MenuItem
        label="DCA Workspace"
        className={dcaBusy ? style.loading : undefined}
        disabled={dcaDisabled}
        onClick={createDcaWorkspace}
      />
    </MenuButton>
  );
};
